import { StateCreator } from 'zustand';
import { BrighterionAuthService } from '@brighterionai/br-auth-service';
import { PIPELINE_ID_SESSION_KEY, constructUser } from 'Utils';
import { WatchlistScreeningRoleService } from '@brighterionai/br-roles';
import { setAuthService, setRoleService } from 'Services';
import { sessionStorageService } from '../services/sessionStorageService';
import { UserSlice } from './user.slice';

import { AuthConfig } from '../types/config';
import { getConfig } from '../config';

export type AuthenticationSlice = {
    appConfig?: any;
    authConfig?: AuthConfig;
    authService?: BrighterionAuthService;
    fetchUser: () => Promise<void>;
    fetchConfig: () => Promise<void>;
    isAuthenticated: boolean;
    logout: () => Promise<void>;
    roles: string[];
    userDetails?: User;
};

export const createAuthenticationSlice: StateCreator<AuthenticationSlice & UserSlice, [], [], AuthenticationSlice> = (
    set,
    get
) => ({
    appConfig: undefined,
    authConfig: undefined,
    authService: undefined,

    fetchUser: async () => {
        set({ isAuthenticated: false });
        /**
         * If user refresh the browser while impersonating the Tenant and
         * the access token is invalid. The App will redirect the user to Okta,
         * and Okta returns the access token from before impersonation.
         * To aviod any config conflicts remove items from the session Storage.
         */
        if (!sessionStorage.getItem('spring-auth-access-token')) {
            sessionStorage.removeItem(PIPELINE_ID_SESSION_KEY);
        }

        const auth = get().authConfig;
        const authService = new BrighterionAuthService(auth!.type, auth);
        await authService.init();

        const userInfo = await authService.getUserInfo();
        const userDetails = constructUser(userInfo);
        const roleService = new WatchlistScreeningRoleService(userDetails.roles);

        setRoleService(roleService, userDetails);
        setAuthService(authService);

        // Update both authentication slice and user slice
        set({
            authService,
            isAuthenticated: true,
            roles: { ...roleService.roles },
            userDetails
        });

        // Update the user slice with the latest user details
        const { setUser } = get();
        if (setUser) {
            setUser(userDetails);
        }
    },

    fetchConfig: async () => {
        const config = await getConfig();
        const { auth, ...appConfig } = config;
        set({ authConfig: auth, appConfig });
    },

    isAuthenticated: false,

    logout: async () => {
        const auth = get().authService;
        if (auth) {
            await auth.logout();
            set({ isAuthenticated: false });
            // Use clearAll() for logout to remove everything including auth tokens
            sessionStorageService.clearAll();
            localStorage.clear();
            window.location.replace('/');
        } else {
            throw new Error('Logout failed: BrighterionAuthService is `undefined`');
        }
    },

    roles: [],
    userDetails: undefined
});
